import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { TransportManagementProfileItemService } from './transport-management-profile-item.service';
import {
  TransportManagementProfileItemCreate,
  TransportManagementProfileItemInitialState,
  TransportManagementProfileItemLoad,
  TransportManagementProfileItemUpdate,
} from './transport-management-profile-item.actions';
import { TransportManagementProfileModel } from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface TransportManagementProfileItemStateModel extends TransportManagementProfileModel {
  loaded: boolean;
}

const initialState: TransportManagementProfileItemStateModel = {
  ...new TransportManagementProfileModel(),
  loaded: false,
};

@State<TransportManagementProfileItemStateModel>({
  name: 'transportManagementPlannerProfileItem',
  defaults: initialState,
})
@Injectable()
export class TransportManagementProfileItemState {
  constructor(
    private transportManagementProfileItemService: TransportManagementProfileItemService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getProfileId(state: TransportManagementProfileItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getState(state: TransportManagementProfileItemStateModel): TransportManagementProfileItemStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: TransportManagementProfileItemStateModel): boolean {
    return state.loaded;
  }

  @Action(TransportManagementProfileItemLoad)
  getTransportManagementProfileItem(
    ctx: StateContext<TransportManagementProfileItemStateModel>,
    { payload }: TransportManagementProfileItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementProfileItemService.getTransportManagementProfile(payload).pipe(
      map((transportPlannerProfile: TransportManagementProfileModel) => {
        ctx.patchState({
          ...new TransportManagementProfileModel(transportPlannerProfile),
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementProfileItemCreate)
  createTransportManagementProfileItem(
    ctx: StateContext<TransportManagementProfileItemStateModel>,
    { payload }: TransportManagementProfileItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementProfileItemService.createTransportManagementProfile(payload.profile).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.transportManagementProfile.success.create');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementProfileItemUpdate)
  updateTransportManagementProfileItem(
    ctx: StateContext<TransportManagementProfileItemStateModel>,
    { payload }: TransportManagementProfileItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportManagementProfileItemService.updateTransportManagementProfile(payload).pipe(
      map((updatedProfile: TransportManagementProfileModel) => {
        ctx.patchState({ ...updatedProfile });
        this.notificationService.success('api.transportManagementProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportManagementProfileItemInitialState)
  setInitialState(ctx: StateContext<TransportManagementProfileItemStateModel>): void {
    ctx.setState(initialState);
  }
}
